import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const AllFlightsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [flights, setFlights] = useState([]);
  const [filters, setFilters] = useState({
    priceRange: [0, 2000],
    airlines: [],
    departureTime: 'any',
    stops: 'any',
    duration: 'any'
  });
  const [sortBy, setSortBy] = useState('price');
  const [showFilters, setShowFilters] = useState(false);

  // Get search parameters
  const searchCriteria = {
    from: searchParams.get('from') || 'JFK',
    to: searchParams.get('to') || 'LAX',
    departureDate: searchParams.get('date') || '2024-03-20',
    passengers: parseInt(searchParams.get('passengers')) || 1,
    class: searchParams.get('class') || 'economy'
  };

  useEffect(() => {
    const fetchFlights = async () => {
      setLoading(true);
      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Mock flight data
        const mockFlights = [
          {
            id: 1,
            flightNumber: 'JS 1234',
            airline: 'JetScanner Airways',
            aircraft: 'Boeing 737-800',
            departure: {
              airport: 'JFK',
              city: 'New York',
              time: '08:30',
              terminal: 'T4'
            },
            arrival: {
              airport: 'LAX',
              city: 'Los Angeles',
              time: '11:45',
              terminal: 'T1'
            },
            duration: '5h 15m',
            stops: 0,
            price: 450,
            class: 'Economy',
            totalSeats: 189,
            availableSeats: 23,
            amenities: ['WiFi', 'Meals', 'Entertainment'],
            baggage: '1 x 23kg included'
          },
          {
            id: 2,
            flightNumber: 'AA 5678',
            airline: 'American Airlines',
            aircraft: 'Airbus A321',
            departure: {
              airport: 'JFK',
              city: 'New York',
              time: '10:15',
              terminal: 'T8'
            },
            arrival: {
              airport: 'LAX',
              city: 'Los Angeles',
              time: '13:45',
              terminal: 'T4'
            },
            duration: '5h 30m',
            stops: 0,
            price: 520,
            class: 'Economy',
            totalSeats: 200,
            availableSeats: 15,
            amenities: ['WiFi', 'Snacks', 'Entertainment'],
            baggage: '1 x 23kg included'
          },
          {
            id: 3,
            flightNumber: 'DL 9012',
            airline: 'Delta Air Lines',
            aircraft: 'Boeing 757-200',
            departure: {
              airport: 'JFK',
              city: 'New York',
              time: '14:20',
              terminal: 'T2'
            },
            arrival: {
              airport: 'LAX',
              city: 'Los Angeles',
              time: '17:55',
              terminal: 'T2'
            },
            duration: '5h 35m',
            stops: 0,
            price: 380,
            class: 'Economy',
            totalSeats: 199,
            availableSeats: 8,
            amenities: ['WiFi', 'Meals', 'Entertainment', 'Power Outlets'],
            baggage: '1 x 23kg included'
          },
          {
            id: 4,
            flightNumber: 'UA 3456',
            airline: 'United Airlines',
            aircraft: 'Boeing 777-200',
            departure: {
              airport: 'JFK',
              city: 'New York',
              time: '16:45',
              terminal: 'T7'
            },
            arrival: {
              airport: 'LAX',
              city: 'Los Angeles',
              time: '20:30',
              terminal: 'T7'
            },
            duration: '5h 45m',
            stops: 0,
            price: 680,
            class: 'Economy',
            totalSeats: 312,
            availableSeats: 31,
            amenities: ['WiFi', 'Meals', 'Entertainment', 'Power Outlets', 'Extra Legroom'],
            baggage: '1 x 23kg included'
          },
          {
            id: 5,
            flightNumber: 'SW 7890',
            airline: 'Southwest Airlines',
            aircraft: 'Boeing 737-700',
            departure: {
              airport: 'JFK',
              city: 'New York',
              time: '19:10',
              terminal: 'T5'
            },
            arrival: {
              airport: 'LAX',
              city: 'Los Angeles',
              time: '22:40',
              terminal: 'T1'
            },
            duration: '5h 30m',
            stops: 0,
            price: 320,
            class: 'Economy',
            totalSeats: 143,
            availableSeats: 42,
            amenities: ['WiFi', 'Snacks'],
            baggage: '2 x 23kg included'
          }
        ];

        setFlights(mockFlights);
      } catch (error) {
        console.error('Error fetching flights:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFlights();
  }, [searchParams]);

  const handleFlightSelect = (flight) => {
    navigate(`/flight-details/${flight.id}`, {
      state: {
        flight,
        searchCriteria,
        returnUrl: window.location.pathname + window.location.search
      }
    });
  };

  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
    const sortedFlights = [...flights].sort((a, b) => {
      switch (newSortBy) {
        case 'price':
          return a.price - b.price;
        case 'duration':
          return parseInt(a.duration) - parseInt(b.duration);
        case 'departure':
          return a.departure.time.localeCompare(b.departure.time);
        case 'arrival':
          return a.arrival.time.localeCompare(b.arrival.time);
        default:
          return 0;
      }
    });
    setFlights(sortedFlights);
  };

  const getTimeOfDay = (time) => {
    const hour = parseInt(time.split(':')[0]);
    if (hour < 6) return 'Early Morning';
    if (hour < 12) return 'Morning';
    if (hour < 18) return 'Afternoon';
    return 'Evening';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-jet-gray-50 pt-[70px]">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jet-yellow mx-auto mb-4"></div>
            <p className="text-jet-gray-600">Searching for flights...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-jet-gray-50 pt-[70px]">
      <main className="py-8">
        <div className="w-[86%] mx-auto">
          {/* Search Summary */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-2xl font-bold text-jet-gray-900">
                  {searchCriteria.from} → {searchCriteria.to}
                </h1>
                <p className="text-jet-gray-600">
                  {formatDate(searchCriteria.departureDate)} • {searchCriteria.passengers} passenger{searchCriteria.passengers > 1 ? 's' : ''} • {searchCriteria.class}
                </p>
              </div>
              <button
                onClick={() => navigate('/search-flight')}
                className="flex items-center px-4 py-2 border border-jet-gray-300 rounded-md text-jet-gray-700 hover:bg-jet-gray-50 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 17l-5-5m0 0l5-5m-5 5h12"></path>
                </svg>
                Modify Search
              </button>
            </div>

            <div className="flex items-center justify-between">
              <p className="text-sm text-jet-gray-600">
                {flights.length} flights found
              </p>

              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <label className="text-sm font-medium text-jet-gray-700">Sort by:</label>
                  <select
                    value={sortBy}
                    onChange={(e) => handleSortChange(e.target.value)}
                    className="border border-jet-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:border-jet-yellow"
                  >
                    <option value="price">Price (Low to High)</option>
                    <option value="duration">Duration</option>
                    <option value="departure">Departure Time</option>
                    <option value="arrival">Arrival Time</option>
                  </select>
                </div>

                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center px-3 py-1 border border-jet-gray-300 rounded-md text-sm text-jet-gray-700 hover:bg-jet-gray-50 transition-colors"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z"></path>
                  </svg>
                  Filters
                </button>
              </div>
            </div>
          </div>

          {/* Flight Results */}
          <div className="space-y-4">
            {flights.map((flight) => (
              <div key={flight.id} className="bg-white rounded-lg shadow-sm border border-jet-gray-200 hover:border-jet-yellow transition-colors">
                <div className="p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-center">
                    {/* Flight Info */}
                    <div className="lg:col-span-2">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-jet-gray-900">{flight.flightNumber}</h3>
                          <p className="text-sm text-jet-gray-600">{flight.airline} • {flight.aircraft}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-jet-gray-600">Available Seats</p>
                          <p className="font-medium text-jet-gray-900">{flight.availableSeats}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-4 items-center">
                        {/* Departure */}
                        <div>
                          <p className="text-2xl font-bold text-jet-gray-900">{flight.departure.time}</p>
                          <p className="text-sm font-medium text-jet-gray-700">{flight.departure.airport}</p>
                          <p className="text-xs text-jet-gray-500">{flight.departure.city}</p>
                          <p className="text-xs text-jet-gray-500">Terminal {flight.departure.terminal}</p>
                        </div>

                        {/* Flight Path */}
                        <div className="text-center">
                          <div className="flex items-center justify-center mb-1">
                            <div className="flex-1 border-t border-jet-gray-300"></div>
                            <div className="px-2">
                              <svg className="w-5 h-5 text-jet-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path>
                              </svg>
                            </div>
                            <div className="flex-1 border-t border-jet-gray-300"></div>
                          </div>
                          <p className="text-xs text-jet-gray-600">{flight.duration}</p>
                          <p className="text-xs text-jet-gray-500">
                            {flight.stops === 0 ? 'Non-stop' : `${flight.stops} stop${flight.stops > 1 ? 's' : ''}`}
                          </p>
                        </div>

                        {/* Arrival */}
                        <div className="text-right">
                          <p className="text-2xl font-bold text-jet-gray-900">{flight.arrival.time}</p>
                          <p className="text-sm font-medium text-jet-gray-700">{flight.arrival.airport}</p>
                          <p className="text-xs text-jet-gray-500">{flight.arrival.city}</p>
                          <p className="text-xs text-jet-gray-500">Terminal {flight.arrival.terminal}</p>
                        </div>
                      </div>

                      {/* Amenities */}
                      <div className="mt-4 flex flex-wrap gap-2">
                        {flight.amenities.slice(0, 4).map((amenity, index) => (
                          <span key={index} className="inline-block bg-jet-blue-100 text-jet-blue-800 text-xs px-2 py-1 rounded-full">
                            {amenity}
                          </span>
                        ))}
                        {flight.amenities.length > 4 && (
                          <span className="inline-block bg-jet-gray-100 text-jet-gray-600 text-xs px-2 py-1 rounded-full">
                            +{flight.amenities.length - 4} more
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Price and Actions */}
                    <div className="lg:col-span-2 flex items-center justify-between">
                      <div>
                        <p className="text-xs text-jet-gray-500 mb-1">from</p>
                        <p className="text-3xl font-bold text-jet-gray-900">${flight.price}</p>
                        <p className="text-sm text-jet-gray-600">per person</p>
                        <p className="text-xs text-jet-gray-500 mt-1">{flight.baggage}</p>
                      </div>

                      <div className="text-right space-y-2">
                        <button
                          onClick={() => handleFlightSelect(flight)}
                          className="w-full bg-jet-yellow hover:bg-jet-yellow-hover text-jet-gray-900 font-medium py-3 px-6 rounded-md transition-colors"
                        >
                          Select Flight
                        </button>
                        <button
                          onClick={() => navigate(`/flight-details/${flight.id}`)}
                          className="w-full border border-jet-gray-300 hover:border-jet-gray-400 text-jet-gray-700 font-medium py-2 px-6 rounded-md transition-colors text-sm"
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Time of Day Badge */}
                <div className="px-6 pb-4">
                  <span className="inline-block bg-jet-gray-100 text-jet-gray-700 text-xs px-3 py-1 rounded-full">
                    {getTimeOfDay(flight.departure.time)} Departure
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Load More */}
          <div className="text-center mt-8">
            <button className="bg-white border border-jet-gray-300 hover:border-jet-gray-400 text-jet-gray-700 font-medium py-3 px-8 rounded-md transition-colors">
              Load More Flights
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AllFlightsPage;